import { useState, useEffect } from "react";
import axios from "axios";
import EChartsReact from "echarts-for-react";
import Select, { components } from "react-select";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./Dettaglio.css";
import "../Generale/Generale.css";
import Lf23Loader from "../../../components/Lf23Loader";

// Componente per mostrare max 2 elementi + badge "+N"
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    if (index < 2) {
        return (
            <components.MultiValue selectProps={selectProps} data={data} {...props}>
                {children}
            </components.MultiValue>
        );
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="spazzato-dettaglio-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

// Opzioni per i dropdown
const colonneOptions = [
    { value: 'mese', label: 'Mese' },
    { value: 'trimestre', label: 'Trimestre' },
    { value: 'anno', label: 'Anno' }
];

const righeOptions = [
    { value: 'settore', label: 'Settore' },
    { value: 'commessa', label: 'Commessa' },
    { value: 'attivita_3', label: 'Attività Liv.3' },
    { value: 'attivita_4', label: 'Attività Liv.4' }
];

const indicatoreOptions = [
    { value: 'km', label: 'Km Spazzati' },
    { value: 'ore', label: 'Ore Spazzate' }
];



const SpazzatoDettaglio = ({ filters, loading: parentLoading = false }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // stati
    const [data, setData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);

    // stati per i dropdown
    const [selectedColonne, setSelectedColonne] = useState([colonneOptions[0]]);
    const [selectedRighe, setSelectedRighe] = useState([righeOptions[0]]);
    const [selectedIndicatore, setSelectedIndicatore] = useState([...indicatoreOptions]);

    // stati per i grafici
    const [comboChartData, setComboChartData] = useState({ monthly: {}, quarterly: {}, yearly: {} });
    const [scatterChartData, setScatterChartData] = useState([]);
    const [regressionLineData, setRegressionLineData] = useState(null);

    // stato per la vista del grafico combo (mese/trimestre/anno)
    const [chartViewType, setChartViewType] = useState('quarterly'); // default trimestrale

    // Stato per il periodo selezionato dal click sul grafico combo
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    // Stato per la commessa selezionata dal click sullo scatter
    const [selectedCommessa, setSelectedCommessa] = useState(null);

    // Stato per le righe espanse (tiene traccia di quali righe sono aperte)
    const [expandedRows, setExpandedRows] = useState([]);

    const [isFullScreenTable, setIsFullScreenTable] = useState(false);
    const [isFullscreenCombo, setIsFullscreenCombo] = useState(false);
    const [isFullscreenScatter, setIsFullscreenScatter] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullScreenTable(false);
                setIsFullscreenCombo(false);
                setIsFullscreenScatter(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullScreenTable, isFullscreenCombo, isFullscreenScatter]);

    // Funzione per formattare i numeri in stile italiano
    const formatNumber = (num) => {
        if (num === null || num === undefined || isNaN(num)) return '0,00';
        return num.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Funzione per aprire/chiudere una riga
    const toggleRowExpansion = (rowKey) => {
        if (expandedRows.includes(rowKey)) {
            // Se la riga è già aperta, la chiudo
            setExpandedRows(expandedRows.filter(id => id !== rowKey));
        } else {
            // Se la riga è chiusa, la apro
            setExpandedRows([...expandedRows, rowKey]);
        }
    };

    // Funzione per verificare se una riga è espansa
    const isRowExpanded = (rowKey) => {
        return expandedRows.includes(rowKey);
    };

    // funzione per convertire il numero del mese nel nome
    const getNomeMese = (mese) => {
        const nomiMesi = [
            "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
            "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
        ];
        return nomiMesi[mese - 1];
    };

    // funzione per trasformare i dati in base alle selezioni
    const trasformaDati = () => {
        if (!data || data.length === 0) return { righeGerarchiche: [], colonneStruttura: null };

        // 1. Costruisco la struttura gerarchica delle COLONNE
        // Organizzo: Anno → Trimestre → Mese → Indicatori
        const colonneMap = {};

        data.forEach(row => {
            const anno = row.anno;
            const trimestre = `Q${Math.ceil(row.mese / 3)}`;
            const mese = getNomeMese(row.mese);

            // Creo la gerarchia Anno > Trimestre > Mese
            if (!colonneMap[anno]) {
                colonneMap[anno] = { trimestri: {} };
            }
            if (!colonneMap[anno].trimestri[trimestre]) {
                colonneMap[anno].trimestri[trimestre] = { mesi: {} };
            }
            if (!colonneMap[anno].trimestri[trimestre].mesi[mese]) {
                colonneMap[anno].trimestri[trimestre].mesi[mese] = true;
            }
        });

        // 2. Determino quali livelli di colonne mostrare
        const mostraAnno = selectedColonne.some(opt => opt.value === 'anno');
        const mostraTrimestre = selectedColonne.some(opt => opt.value === 'trimestre');
        const mostraMese = selectedColonne.some(opt => opt.value === 'mese');

        // Determino il livello di aggregazione dei dati
        const livelloAggregazione = mostraMese ? 'mese' : mostraTrimestre ? 'trimestre' : 'anno';

        // 3. Creo l'array degli indicatori selezionati
        const indicatoriSelezionati = selectedIndicatore.map(opt => ({
            value: opt.value,
            label: opt.label
        }));
        if (indicatoriSelezionati.length === 0) {
            indicatoriSelezionati.push({ value: 'km', label: 'Km Spazzati' });
        }

        // 4. Costruisco la struttura per il rendering dell'header
        const colonneStruttura = {
            anni: [],
            mostraAnno,
            mostraTrimestre,
            mostraMese,
            indicatori: indicatoriSelezionati
        };

        // Ordino gli anni
        const anniOrdinati = Object.keys(colonneMap).sort();

        anniOrdinati.forEach(anno => {
            const annoObj = {
                nome: anno,
                trimestri: [],
                colSpan: 0
            };

            if (livelloAggregazione === 'anno') {
                // Aggregazione a livello anno: nessun dettaglio trimestre/mese
                annoObj.colSpan = indicatoriSelezionati.length;
            } else {
                // Ordino i trimestri
                const trimOrdinati = Object.keys(colonneMap[anno].trimestri).sort();

                trimOrdinati.forEach(trimestre => {
                    const trimObj = {
                        nome: `${trimestre} ${anno}`,
                        chiave: `${anno}-${trimestre}`,
                        mesi: [],
                        colSpan: 0
                    };

                    if (livelloAggregazione === 'trimestre') {
                        // Aggregazione a livello trimestre: nessun dettaglio mese
                        trimObj.colSpan = indicatoriSelezionati.length;
                    } else {
                        // Livello mese: dettaglio completo (comportamento originale)
                        const mesiNomi = Object.keys(colonneMap[anno].trimestri[trimestre].mesi);
                        const mesiOrdinati = mesiNomi.sort((a, b) => {
                            const nomiMesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                                "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
                            return nomiMesi.indexOf(a) - nomiMesi.indexOf(b);
                        });

                        mesiOrdinati.forEach(mese => {
                            const meseObj = {
                                nome: `${mese} ${anno}`,
                                chiave: `${anno}-${trimestre}-${mese}`,
                                colSpan: indicatoriSelezionati.length
                            };
                            trimObj.mesi.push(meseObj);
                            trimObj.colSpan += meseObj.colSpan;
                        });
                    }

                    annoObj.trimestri.push(trimObj);
                    annoObj.colSpan += trimObj.colSpan;
                });
            }

            colonneStruttura.anni.push(annoObj);
        });

        // 5. Creo l'array piatto delle chiavi colonna (per i valori)
        const colonneChiavi = [];
        if (livelloAggregazione === 'anno') {
            colonneStruttura.anni.forEach(anno => {
                indicatoriSelezionati.forEach(ind => {
                    colonneChiavi.push({
                        chiave: `${anno.nome}-${ind.value}`,
                        anno: anno.nome,
                        indicatore: ind.value
                    });
                });
            });
        } else if (livelloAggregazione === 'trimestre') {
            colonneStruttura.anni.forEach(anno => {
                anno.trimestri.forEach(trim => {
                    indicatoriSelezionati.forEach(ind => {
                        colonneChiavi.push({
                            chiave: `${trim.chiave}-${ind.value}`,
                            anno: anno.nome,
                            trimestre: trim.nome,
                            indicatore: ind.value
                        });
                    });
                });
            });
        } else {
            colonneStruttura.anni.forEach(anno => {
                anno.trimestri.forEach(trim => {
                    trim.mesi.forEach(mese => {
                        indicatoriSelezionati.forEach(ind => {
                            colonneChiavi.push({
                                chiave: `${mese.chiave}-${ind.value}`,
                                anno: anno.nome,
                                trimestre: trim.nome,
                                mese: mese.nome,
                                indicatore: ind.value
                            });
                        });
                    });
                });
            });
        }

        // 6. Determino i livelli di gerarchia per le RIGHE
        const livelliSelezionati = selectedRighe.map(opt => {
            if (opt.value === 'settore') return 'settore';
            if (opt.value === 'commessa') return 'commessa';
            if (opt.value === 'attivita_3') return 'attivita_livello_3';
            if (opt.value === 'attivita_4') return 'attivita_livello_4';
            return 'settore';
        });

        if (livelliSelezionati.length === 0) {
            livelliSelezionati.push('settore');
        }

        // 7. Costruisco la struttura gerarchica delle RIGHE
        const radice = { children: {}, valori: {} };

        data.forEach(row => {
            const anno = row.anno;
            const trimestre = `Q${Math.ceil(row.mese / 3)}`;
            const mese = getNomeMese(row.mese);

            // Chiave del periodo in base al livello di aggregazione
            let chiavePeriodo;
            if (livelloAggregazione === 'anno') {
                chiavePeriodo = `${anno}`;
            } else if (livelloAggregazione === 'trimestre') {
                chiavePeriodo = `${anno}-${trimestre}`;
            } else {
                chiavePeriodo = `${anno}-${trimestre}-${mese}`;
            }

            // Navigo nella struttura gerarchica delle righe
            let nodoCorrente = radice;

            for (let i = 0; i < livelliSelezionati.length; i++) {
                const campo = livelliSelezionati[i];
                const valoreNodo = row[campo] || 'Non assegnato';

                if (!nodoCorrente.children[valoreNodo]) {
                    nodoCorrente.children[valoreNodo] = {
                        nome: valoreNodo,
                        livello: i,
                        children: {},
                        valori: {}
                    };
                }

                nodoCorrente = nodoCorrente.children[valoreNodo];

                // Aggiungo i valori per ogni indicatore
                indicatoriSelezionati.forEach(ind => {
                    const chiaveValore = `${chiavePeriodo}-${ind.value}`;
                    if (!nodoCorrente.valori[chiaveValore]) {
                        nodoCorrente.valori[chiaveValore] = 0;
                    }

                    let valore = 0;
                    if (ind.value === 'km') {
                        valore = parseFloat(row.km_spazzati || 0);
                    } else {
                        valore = parseFloat(row.ore_spazzate || 0);
                    }
                    nodoCorrente.valori[chiaveValore] += valore;
                });
            }
        });

        // 8. Converto in array piatto per il rendering
        const righeGerarchiche = [];

        const aggiungiRighe = (nodo, livello, chiavePadre) => {
            // Calcolo la somma dei valori per ogni figlio
            const figliConSomma = Object.values(nodo.children).map(figlio => {
                const sommaValori = Object.values(figlio.valori).reduce((acc, val) => acc + val, 0);
                return { ...figlio, sommaValori };
            });

            // Ordino: prima quelli con valori > 0, poi alfabeticamente
            const figli = figliConSomma.sort((a, b) => {
                // Se uno ha valori e l'altro no, quello con valori viene prima
                if (a.sommaValori > 0 && b.sommaValori === 0) return -1;
                if (a.sommaValori === 0 && b.sommaValori > 0) return 1;
                // Se entrambi hanno valori o entrambi non ne hanno, ordino alfabeticamente
                return a.nome.localeCompare(b.nome);
            });

            figli.forEach(figlio => {
                const chiaveRiga = chiavePadre ? `${chiavePadre}|${figlio.nome}` : figlio.nome;
                const haFigli = Object.keys(figlio.children).length > 0;

                righeGerarchiche.push({
                    chiave: chiaveRiga,
                    nome: figlio.nome,
                    livello: livello,
                    valori: figlio.valori,
                    haFigli: haFigli
                });

                if (haFigli && isRowExpanded(chiaveRiga)) {
                    aggiungiRighe(figlio, livello + 1, chiaveRiga);
                }
            });
        };

        aggiungiRighe(radice, 0, '');

        return { righeGerarchiche, colonneStruttura, colonneChiavi };
    }


    // Fetch dati principali (tabella + grafici combo)
    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);

            try {
                // costruisco i parametri dalla prop filters
                const params = new URLSearchParams();

                // Date (obbligatorie)
                if (filters?.fromDate) params.append("fromDate", filters.fromDate);
                if (filters?.toDate) params.append("toDate", filters.toDate);

                // Altri filtri (array di oggetti {value, label})
                // Estraggo solo i value e li unisco con virgola
                if (filters?.settore?.length > 0) {
                    params.append("settore", filters.settore.map(s => s.value).join(','));
                }
                if (filters?.commessa?.length > 0) {
                    params.append("commessa", filters.commessa.map(c => c.value).join(','));
                }
                if (filters?.operatore?.length > 0) {
                    params.append("operatore", filters.operatore.map(o => o.value).join(','));
                }
                if (filters?.tipologiaVeicolo?.length > 0) {
                    params.append("tipologiaVeicolo", filters.tipologiaVeicolo.map(t => t.value).join(','));
                }
                if (filters?.targa?.length > 0) {
                    params.append("targa", filters.targa.map(t => t.value).join(','));
                }

                const response = await axios.get(`${API_URL}/spazzato/dettaglio?${params.toString()}`);
                setData(response.data.data || []);
                setComboChartData(response.data.comboChartData || { monthly: {}, quarterly: {}, yearly: {} });
                setScatterChartData(response.data.scatterChartData || []);
                setRegressionLineData(response.data.regressionLine || null);

                // Reset periodo selezionato quando cambiano i filtri
                setSelectedPeriod(null);

            } catch (error) {
                console.error("Errore nel caricamento dei dati: ", error);
            } finally {
                setDataLoading(false);
            }
        }

        if (filters) {
            fetchData();
        }

    }, [filters, API_URL]);

    // Fetch scatter chart filtrato per periodo quando viene selezionato un periodo
    useEffect(() => {
        if (!filters || !selectedPeriod) {
            return;
        }

        const fetchScatterForPeriod = async () => {
            try {
                const params = new URLSearchParams();

                // Date base
                if (filters?.fromDate) params.append("fromDate", filters.fromDate);
                if (filters?.toDate) params.append("toDate", filters.toDate);

                // Altri filtri
                if (filters?.settore?.length > 0) {
                    params.append("settore", filters.settore.map(s => s.value).join(','));
                }
                if (filters?.commessa?.length > 0) {
                    params.append("commessa", filters.commessa.map(c => c.value).join(','));
                }
                if (filters?.operatore?.length > 0) {
                    params.append("operatore", filters.operatore.map(o => o.value).join(','));
                }
                if (filters?.tipologiaVeicolo?.length > 0) {
                    params.append("tipologiaVeicolo", filters.tipologiaVeicolo.map(t => t.value).join(','));
                }
                if (filters?.targa?.length > 0) {
                    params.append("targa", filters.targa.map(t => t.value).join(','));
                }

                // Aggiungo i parametri del periodo selezionato SOLO se c'è una selezione
                if (selectedPeriod) {
                    if (selectedPeriod.anno) params.append("anno", selectedPeriod.anno);
                    if (selectedPeriod.mese) params.append("mese", selectedPeriod.mese);
                    if (selectedPeriod.trimestre) params.append("trimestre", selectedPeriod.trimestre);
                }

                const response = await axios.get(`${API_URL}/spazzato/dettaglio?${params.toString()}`);
                setScatterChartData(response.data.scatterChartData || []);
                setRegressionLineData(response.data.regressionLine || null);

            } catch (error) {
                console.error("Errore nel caricamento scatter per periodo: ", error);
            }
        };

        fetchScatterForPeriod();
    }, [selectedPeriod, filters, API_URL]);

    // Validazione formato dati
    const hasValidDataFormat = data !== null && Array.isArray(data);

    // Combina loading del padre con validazioni locali
    const isLoading = parentLoading || dataLoading || !hasValidDataFormat;
    const isDataReady = !parentLoading && !dataLoading && hasValidDataFormat && data.length > 0;
    const isEmpty = !parentLoading && !dataLoading && hasValidDataFormat && data.length === 0;

    // FUNZIONI PER ESPORTAZIONE EXCEL 
    // Funzione per scaricare il file Excel
    const handleDownloadFile = async (datiDaEsportare) => {
        try {
            if (!datiDaEsportare) {
                console.error("Nessun dato da esportare");
                return;
            }

            const response = await axios.post(`${API_URL}/spazzato/downloadExcel`,
                {
                    tableName: 'Dettaglio Spazzato',
                    datiDaStampare: datiDaEsportare
                },
                { responseType: "blob" }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "dettaglio_spazzato.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file: ", error);
        }
    };

    // Funzione per preparare i dati da esportare
    const prepareExportData = () => {
        const { righeGerarchiche, colonneChiavi } = trasformaDati();

        if (!righeGerarchiche || righeGerarchiche.length === 0) {
            return null;
        }

        // 1. Creo le intestazioni delle colonne
        const headers = [selectedRighe.length > 0 ? selectedRighe[0].label : 'Settore'];

        // Per ogni colonna, aggiungo l'intestazione con il periodo e l'indicatore
        for (let i = 0; i < colonneChiavi.length; i++) {
            const col = colonneChiavi[i];
            // Estraggo anno, trimestre, mese dalla chiave
            const parts = col.chiave.split('-');
            let periodoLabel = '';

            if (parts.length === 3) {
                // Formato: anno-mese-indicatore
                const anno = parts[0];
                const mese = parts[1];
                periodoLabel = `${getNomeMese(parseInt(mese))} ${anno}`;
            } else if (parts.length === 2 && parts[1].startsWith('trim')) {
                // Formato: anno-trimX-indicatore
                const anno = parts[0];
                const trim = parts[1].replace('trim', 'T');
                periodoLabel = `${trim} ${anno}`;
            } else if (parts.length === 2) {
                // Formato: anno-indicatore
                periodoLabel = parts[0];
            }

            const indicatoreLabel = col.indicatore === 'km' ? 'Km Spazzati' : 'Ore Spazzate';
            headers.push(`${periodoLabel} - ${indicatoreLabel}`);
        }

        // 2. Creo le righe con i dati
        const rows = [];

        // Funzione helper per aggiungere una riga
        const aggiungiRiga = (riga) => {
            // Calcolo l'indentazione in base al livello
            const indent = '  '.repeat(riga.livello);
            const rigaExport = [indent + riga.nome];

            // Aggiungo i valori per ogni colonna
            for (let i = 0; i < colonneChiavi.length; i++) {
                const col = colonneChiavi[i];
                const valore = riga.valori[col.chiave] || 0;
                rigaExport.push(valore.toFixed(2));
            }

            rows.push(rigaExport);
        };

        // 3. Ciclo su tutte le righe gerarchiche
        for (let i = 0; i < righeGerarchiche.length; i++) {
            aggiungiRiga(righeGerarchiche[i]);
        }

        // 4. Aggiungo la riga TOTALE (somma solo righe di livello 0)
        const rigaTotale = ['TOTALE'];
        for (let i = 0; i < colonneChiavi.length; i++) {
            const col = colonneChiavi[i];
            let somma = 0;
            righeGerarchiche.forEach(riga => {
                if (riga.livello === 0) {
                    somma += (riga.valori[col.chiave] || 0);
                }
            });
            rigaTotale.push(somma.toFixed(2));
        }
        rows.push(rigaTotale);

        return { headers, rows };
    };

    // Handler per il click sul grafico combo
    const handleComboChartClick = (params) => {
        if (params.componentType === 'series' && params.seriesType === 'bar') {
            const periodoLabel = params.name;

            // Estraggo anno, mese, trimestre dal periodo
            let anno, mese, trimestre;

            if (chartViewType === 'monthly') {
                // Formato: "Gennaio 2025"
                const parts = periodoLabel.split(' ');
                const nomiMesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
                mese = String(nomiMesi.indexOf(parts[0]) + 1);
                anno = parts[1];
            } else if (chartViewType === 'quarterly') {
                // Formato: "Trim 1 2024"
                const parts = periodoLabel.split(' ');
                trimestre = parts[1];
                anno = parts[2];
            } else if (chartViewType === 'yearly') {
                // Formato: "2024"
                anno = periodoLabel;
            }

            // Se clicco sulla stessa barra, deseleziono
            if (selectedPeriod?.label === periodoLabel) {
                setSelectedPeriod(null);
            } else {
                const newPeriod = {
                    label: periodoLabel,
                    anno,
                    mese,
                    trimestre
                };
                setSelectedPeriod(newPeriod);
            }
        }
    };

    // Handler per il click sullo scatter chart
    const handleScatterClick = (params) => {
        if (params.componentType === 'series' && params.seriesType === 'scatter') {
            // params.seriesIndex - 1 perché la prima serie (index 0) è la bisettrice
            const commessaData = scatterChartData[params.seriesIndex - 1];

            if (!commessaData) return;

            // Se clicco sulla stessa commessa, deseleziono
            if (selectedCommessa?.nome === commessaData.nome) {
                setSelectedCommessa(null);
            } else {
                setSelectedCommessa(commessaData);
            }
        }
    };

    // Filtra i dati combo in base alla commessa selezionata
    const getFilteredComboData = () => {
        // Se non c'è una commessa selezionata, usa i dati originali
        if (!selectedCommessa) {
            return comboChartData;
        }

        // Altrimenti, calcola i dati filtrati dalla tabella
        const filteredData = data.filter(row =>
            row.commessa && row.commessa.toLowerCase() === selectedCommessa.nome.toLowerCase()
        );

        // Aggrega per mese
        const monthlyMap = {};
        filteredData.forEach(row => {
            const key = `${row.anno}-${String(row.mese).padStart(2, '0')}`;
            if (!monthlyMap[key]) {
                monthlyMap[key] = { anno: row.anno, mese: row.mese, km: 0, time: 0 };
            }
            monthlyMap[key].km += parseFloat(row.km_spazzati || 0);
            monthlyMap[key].time += parseFloat(row.ore_spazzate || 0) * 3600; // converti ore in secondi
        });

        const monthlyData = Object.keys(monthlyMap).sort().map(key => {
            const item = monthlyMap[key];
            const distMt = item.km * 1000;
            return {
                periodo: `${getNomeMese(item.mese)} ${item.anno}`,
                kmSpazzati: item.km,
                mtAlSecondo: item.time > 0 ? distMt / item.time : 0
            };
        });

        // Aggrega per trimestre
        const quarterlyMap = {};
        filteredData.forEach(row => {
            const trimestre = Math.ceil(row.mese / 3);
            const key = `${row.anno}-Q${trimestre}`;
            if (!quarterlyMap[key]) {
                quarterlyMap[key] = { anno: row.anno, trimestre, km: 0, time: 0 };
            }
            quarterlyMap[key].km += parseFloat(row.km_spazzati || 0);
            quarterlyMap[key].time += parseFloat(row.ore_spazzate || 0) * 3600;
        });

        const quarterlyData = Object.keys(quarterlyMap).sort().map(key => {
            const item = quarterlyMap[key];
            const distMt = item.km * 1000;
            return {
                periodo: `Trim ${item.trimestre} ${item.anno}`,
                kmSpazzati: item.km,
                mtAlSecondo: item.time > 0 ? distMt / item.time : 0
            };
        });

        // Aggrega per anno
        const yearlyMap = {};
        filteredData.forEach(row => {
            const key = `${row.anno}`;
            if (!yearlyMap[key]) {
                yearlyMap[key] = { anno: row.anno, km: 0, time: 0 };
            }
            yearlyMap[key].km += parseFloat(row.km_spazzati || 0);
            yearlyMap[key].time += parseFloat(row.ore_spazzate || 0) * 3600;
        });

        const yearlyData = Object.keys(yearlyMap).sort().map(key => {
            const item = yearlyMap[key];
            const distMt = item.km * 1000;
            return {
                periodo: key,
                kmSpazzati: item.km,
                mtAlSecondo: item.time > 0 ? distMt / item.time : 0
            };
        });

        return {
            monthly: {
                periodi: monthlyData.map(d => d.periodo),
                kmSpazzati: monthlyData.map(d => d.kmSpazzati),
                metriAlSecondo: monthlyData.map(d => d.mtAlSecondo)
            },
            quarterly: {
                periodi: quarterlyData.map(d => d.periodo),
                kmSpazzati: quarterlyData.map(d => d.kmSpazzati),
                metriAlSecondo: quarterlyData.map(d => d.mtAlSecondo)
            },
            yearly: {
                periodi: yearlyData.map(d => d.periodo),
                kmSpazzati: yearlyData.map(d => d.kmSpazzati),
                metriAlSecondo: yearlyData.map(d => d.mtAlSecondo)
            }
        };
    };

    // Grafico 1: Combo Chart (Barre + Linea)
    const getComboDataByView = () => {
        const comboData = getFilteredComboData();
        if (!comboData || !comboData[chartViewType]) {
            return { periodi: [], kmSpazzati: [], metriAlSecondo: [] };
        }
        return comboData[chartViewType];
    };

    const currentComboData = getComboDataByView();

    const comboChartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            formatter: (params) => {
                let result = `<strong>${params[0].name}</strong><br/>`;
                params.forEach(param => {
                    const value = typeof param.value === 'number' ? formatNumber(param.value) : param.value;
                    result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
                });
                return result;
            }
        },
        legend: {
            data: ['Km Spazzati', 'Mt Spazzato al Secondo']
        },
        xAxis: {
            type: 'category',
            data: currentComboData.periodi || []
        },
        yAxis: [
            {
                type: 'value',
                name: 'Km Spazzati',
                position: 'left'
            },
            {
                type: 'value',
                name: 'Mt al Secondo',
                position: 'right'
            }
        ],
        series: [
            {
                name: 'Km Spazzati',
                type: 'bar',
                z: 1,
                data: currentComboData.kmSpazzati?.map((value, index) => ({
                    value,
                    itemStyle: {
                        color: '#2c3e50',
                        opacity: selectedPeriod && currentComboData.periodi[index] !== selectedPeriod.label
                            ? 0.3
                            : 1.0
                    }
                })) || []
            },
            {
                name: 'Mt Spazzato al Secondo',
                type: 'line',
                z: 2,
                yAxisIndex: 1,
                data: currentComboData.metriAlSecondo || [],
                itemStyle: { color: '#95c11f' },
                lineStyle: { type: 'dashed' }
            },
            {
                // Serie invisibile solo per le label, sopra la linea
                type: 'bar',
                z: 3,
                data: currentComboData.kmSpazzati?.map((value) => ({
                    value,
                    itemStyle: { color: 'transparent' }
                })) || [],
                barGap: '-100%',
                label: {
                    show: true,
                    position: 'top',
                    formatter: (params) => formatNumber(params.value),
                    fontSize: 11,
                    fontWeight: 'bold',
                    color: '#2c3e50'
                },
                silent: true,
                tooltip: { show: false }
            }
        ]
    };

    // Grafico 2: Scatter Chart (Efficienza per Commessa)

    const scatterChartOption = {
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                // Sottraggo 1 perché la linea di tendenza è la prima serie (index 0)
                const item = scatterChartData[params.seriesIndex - 1] || {};
                return `${item.nome || ''}<br/>` +
                    `Distanza: ${formatNumber(item.distanzaKm || 0)} km<br/>` +
                    `Spazzato: ${formatNumber(item.spazzatoKm || 0)} km<br/>` +
                    `Efficienza: ${formatNumber(item.mtAlSecondo || 0)} m/s`;
            }
        },
        legend: {
            data: scatterChartData.map(item => item.nome),
            type: 'scroll',
            bottom: 0
        },
        xAxis: {
            type: 'value',
            name: 'Distanza Km',
            nameLocation: 'middle',
            nameGap: 30,
            min: 0,
            max: scatterChartData.length > 0
                ? Math.ceil(Math.max(...scatterChartData.map(item => item.distanzaKm)) * 1.2)
                : undefined,
            axisLabel: {
                formatter: (value) => formatNumber(value)
            }
        },
        yAxis: {
            type: 'value',
            name: 'Km Spazzati',
            nameLocation: 'middle',
            nameGap: 40,
            min: 0,
            max: scatterChartData.length > 0
                ? Math.ceil(Math.max(...scatterChartData.map(item => item.spazzatoKm)) * 1.2)
                : undefined,
            axisLabel: {
                formatter: (value) => formatNumber(value)
            }
        },
        series: [
            // Linea di regressione (trend line)
            {
                name: 'Linea di tendenza',
                type: 'line',
                data: regressionLineData || [],
                lineStyle: {
                    color: '#999',
                    width: 2,
                    type: 'dashed'
                },
                itemStyle: {
                    color: '#999'
                },
                symbol: 'none',
                silent: true,
                z: 0
            },
            // Serie scatter (commesse)
            ...scatterChartData.map(item => ({
                name: item.nome,
                type: 'scatter',
                data: [[item.distanzaKm, item.spazzatoKm]],
                symbolSize: Math.max(10, (item.mtAlSecondo || 0) * 5),
                itemStyle: {
                    color: item.color,
                    opacity: selectedCommessa && item.nome !== selectedCommessa.nome
                        ? 0.3  // Opacizzare i pallini NON selezionati
                        : 1.0  // Normale se selezionato o nessuna selezione
                },
                label: {
                    show: true,
                    position: 'right',
                    formatter: () => item.nome,
                    fontSize: 11,
                    fontWeight: '600',
                    color: item.color,
                    offset: [5, 0]
                },
                z: 10
            }))
        ]
    };

    const { righeGerarchiche, colonneStruttura, colonneChiavi } = trasformaDati();

    // Calcolo dei totali per ogni colonna
    const calcolaTotali = () => {
        const totali = {};

        // Controllo se colonneChiavi esiste ed è un array
        if (!colonneChiavi || !Array.isArray(colonneChiavi)) {
            return totali;
        }

        // Per ogni colonna, sommo solo le righe di livello 0 (evita double-counting con righe espanse)
        colonneChiavi.forEach(col => {
            let somma = 0;
            if (righeGerarchiche && Array.isArray(righeGerarchiche)) {
                righeGerarchiche.forEach(riga => {
                    if (riga.livello === 0) {
                        somma += (riga.valori[col.chiave] || 0);
                    }
                });
            }
            totali[col.chiave] = somma;
        });

        return totali;
    };

    const totaliRow = calcolaTotali();

    return (
        <div className="spazzato-dettaglio-container">
            {isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="oper-hr-empty-state">
                    <div className="oper-hr-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                    <p className="oper-hr-empty-text">
                        Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.
                    </p>
                </div>
            ) : isDataReady ? (
                <>
                    {/* GRAFICI */}
                    <div className="spazzato-dettaglio-charts-row">
                        {/* Grafico 1: Combo Chart */}
                        <div className={`spazzato-dettaglio-chart-wrapper${isFullscreenCombo ? ' fullscreen-chart' : ''}`}>
                            <RotateDevice active={isFullscreenCombo} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="spazzato-generale-table-title">Totale Spazzato e Spazzato al secondo per Periodo</span>
                                {/* Selettore vista grafico combo */}
                                <div className="oper-hr-table-title-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div className="oper-hr-table-view-type-container">
                                        <button
                                            type="button"
                                            className={`oper-hr-table-view-btn ${chartViewType === 'monthly' ? 'active' : ''}`}
                                            onClick={() => setChartViewType('monthly')}
                                            title="Vista Mensile"
                                        >
                                            <span>M</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`oper-hr-table-view-btn ${chartViewType === 'quarterly' ? 'active' : ''}`}
                                            onClick={() => setChartViewType('quarterly')}
                                            title="Vista Trimestrale"
                                        >
                                            <span>T</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`oper-hr-table-view-btn ${chartViewType === 'yearly' ? 'active' : ''}`}
                                            onClick={() => setChartViewType('yearly')}
                                            title="Vista Annuale"
                                        >
                                            <span>A</span>
                                        </button>
                                        <button
                                            className="dettaglio-chart-fullscreen-btn"
                                            onClick={() => setIsFullscreenCombo(!isFullscreenCombo)}
                                            title={isFullscreenCombo ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                {isFullscreenCombo ? (
                                                    <>
                                                        <polyline points="4 14 10 14 10 20"></polyline>
                                                        <polyline points="20 10 14 10 14 4"></polyline>
                                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                ) : (
                                                    <>
                                                        <polyline points="15 3 21 3 21 9"></polyline>
                                                        <polyline points="9 21 3 21 3 15"></polyline>
                                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <EChartsReact
                                key={selectedCommessa ? `combo-${selectedCommessa.nome}` : 'combo-all'}
                                option={comboChartOption}
                                style={{ height: '350px', width: '100%' }}
                                notMerge={true}
                                lazyUpdate={false}
                                onEvents={{ click: handleComboChartClick }}
                            />
                        </div>

                        {/* Grafico 2: Scatter Chart */}
                        <div className={`spazzato-dettaglio-chart-wrapper${isFullscreenScatter ? ' fullscreen-chart' : ''}`}>
                            <RotateDevice active={isFullscreenScatter} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="spazzato-generale-table-title">Efficienza Spazzato per Commessa</span>
                                <button
                                    className="dettaglio-chart-fullscreen-btn"
                                    onClick={() => setIsFullscreenScatter(!isFullscreenScatter)}
                                    title={isFullscreenScatter ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenScatter ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <EChartsReact
                                option={scatterChartOption}
                                style={{ height: '350px', width: '100%' }}
                                onEvents={{ click: handleScatterClick }}
                            />
                        </div>
                    </div>


                    {/* Tabella */}
                    <div className={`spazzato-dettaglio-table-section${isFullScreenTable ? ' fullscreen-table' : ''}`}>
                        <RotateDevice active={isFullScreenTable} />
                        <div className="spazzato-dettaglio-table-header">
                            <span className="spazzato-dettaglio-table-title">Dettaglio per attività</span>
                            <div className="spazzato-dettaglio-table-header-btn">
                                <button
                                    className="spazzato-dettaglio-download-btn"
                                    onClick={() => handleDownloadFile(prepareExportData())}
                                    disabled={data.length === 0}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>

                                <button
                                    className="dettaglio-table-fullscreen-btn"
                                    onClick={() => setIsFullScreenTable(!isFullScreenTable)}
                                    title={isFullScreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullScreenTable ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* dropdown */}
                        <div className="spazzato-dettaglio-config-row">
                            <div className="spazzato-dettaglio-config-item">
                                <label>COLONNE</label>
                                <Select
                                    classNamePrefix="spazzato-dettaglio-select"
                                    placeholder="Seleziona colonne..."
                                    value={selectedColonne}
                                    options={colonneOptions}
                                    onChange={setSelectedColonne}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ MultiValue: MultiValueContainer }}
                                />
                            </div>

                            <div className="spazzato-dettaglio-config-item">
                                <label>RIGHE</label>
                                <Select
                                    classNamePrefix="spazzato-dettaglio-select"
                                    placeholder="Seleziona righe..."
                                    value={selectedRighe}
                                    options={righeOptions}
                                    onChange={setSelectedRighe}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ MultiValue: MultiValueContainer }}
                                />
                            </div>

                            <div className="spazzato-dettaglio-config-item">
                                <label>INDICATORE</label>
                                <Select
                                    classNamePrefix="spazzato-dettaglio-select"
                                    placeholder="Seleziona indicatori..."
                                    value={selectedIndicatore}
                                    options={indicatoreOptions}
                                    onChange={setSelectedIndicatore}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ MultiValue: MultiValueContainer }}
                                />
                            </div>
                        </div>
                        <div className="spazzato-dettaglio-table-wrapper">
                            <table className="spazzato-dettaglio-table">
                                <thead>
                                    {/* Riga ANNO */}
                                    {colonneStruttura && colonneStruttura.mostraAnno && (
                                        <tr>
                                            <th className="th-settore">ANNO</th>
                                            {colonneStruttura.anni.map(anno => (
                                                <th key={anno.nome} className="th-periodo" colSpan={anno.colSpan}>
                                                    {anno.nome}
                                                </th>
                                            ))}
                                        </tr>
                                    )}

                                    {/* Riga TRIMESTRE */}
                                    {colonneStruttura && colonneStruttura.mostraTrimestre && (
                                        <tr>
                                            <th className="th-settore">TRIMESTRE</th>
                                            {colonneStruttura.anni.map(anno => (
                                                anno.trimestri.map(trim => (
                                                    <th key={trim.nome} className="th-periodo" colSpan={trim.colSpan}>
                                                        {trim.nome}
                                                    </th>
                                                ))
                                            ))}
                                        </tr>
                                    )}

                                    {/* Riga MESE */}
                                    {colonneStruttura && colonneStruttura.mostraMese && (
                                        <tr>
                                            <th className="th-settore">MESE</th>
                                            {colonneStruttura.anni.map(anno => (
                                                anno.trimestri.map(trim => (
                                                    trim.mesi.map(mese => (
                                                        <th key={mese.chiave} className="th-periodo" colSpan={mese.colSpan}>
                                                            {mese.nome}
                                                        </th>
                                                    ))
                                                ))
                                            ))}
                                        </tr>
                                    )}

                                    {/* Riga INDICATORI (sempre visibile) */}
                                    {colonneStruttura && (
                                        <tr>
                                            <th className="th-settore">
                                                {selectedRighe.length > 0 ? selectedRighe[0].label.toUpperCase() : "SETTORE"}
                                            </th>
                                            {colonneChiavi.map((col, idx) => (
                                                <th key={idx} className="th-metrica">
                                                    {col.indicatore === 'km' ? 'Km Spazzati' : 'Ore Spazzate'}
                                                </th>
                                            ))}
                                        </tr>
                                    )}
                                </thead>
                                <tbody>
                                    {righeGerarchiche.map((riga) => (
                                        <tr key={riga.chiave} className={`tr-level-${riga.livello}`}>
                                            <td
                                                className={`td-settore td-indent-${riga.livello} ${riga.haFigli ? 'td-expandable' : ''}`}
                                                onClick={riga.haFigli ? () => toggleRowExpansion(riga.chiave) : undefined}
                                            >
                                                {riga.haFigli ? (
                                                    <span className="expand-icon">
                                                        {isRowExpanded(riga.chiave) ? '▼' : '▶'}
                                                    </span>
                                                ) : (
                                                    <span className="expand-icon" style={{ visibility: 'hidden' }}>▶</span>
                                                )}
                                                <span className="level-value">{riga.nome}</span>
                                            </td>
                                            {colonneChiavi.map((col, idx) => (
                                                <td key={idx}>
                                                    {formatNumber(riga.valori[col.chiave] || 0)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                    {/* Riga TOTALE */}
                                    <tr className="tr-totale">
                                        <td className="td-settore td-totale">
                                            <span className="level-value">TOTALE</span>
                                        </td>
                                        {colonneChiavi.map((col, idx) => (
                                            <td key={idx} className="td-totale">
                                                {formatNumber(totaliRow[col.chiave] || 0)}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default SpazzatoDettaglio;